import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone } from "lucide-react";
import "./staffPage.css";

import usersRequest from "../../request/usersRequest";
import generalRequest from "../../request/generalRequest";
import StaffPageSkeleton from "./staffSkeleton/staffPageSkeleton";
import BookingModal from "../bookingModal/bookingModal";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";

const StaffPage = () => {
    const { shopId, staffId } = useParams();
    const { selectedService, setSelectedService, selectedstaff, setSelectedstaff, language } =
        useContext(CombinedContext);

    const [staff, setStaff] = useState(null);
    const [shopData, setShopData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isBookingOpen, setIsBookingOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const categoryRefs = useRef({});

    const navigate = useNavigate();

    useEffect(() => {
        if (!staffId) return;

        const load = async () => {
            try {
                const res = await generalRequest.getStaffInfoPublic(staffId);

                const s = res.staff;

                // Staff info
                setStaff({
                    staffId: s.staffId,
                    firstName: s.firstName,
                    lastName: s.lastName,
                    image: s.photo || null,
                    bio: s.bio || "",
                    phone: s.phone || "",
                    services: s.services || [],
                });

                // Shop info (from API)
                setShopData(s.shop);

                setLoading(false);
            } catch (err) {
                console.error("Failed to fetch staff:", err);
                setLoading(false);
            }
        };

        load();
    }, [staffId]);

    const servicesByCategory = (shopData?.serviceCategories || [])
        .map((cat) => ({
            category: cat,
            services: staff.services.filter((s) => s.categoryId === cat.categoryId),
        }))
        .filter(({ services }) => services.length > 0);

    useEffect(() => {
        if (!servicesByCategory.length) return;

        const stillValid = servicesByCategory.some((group) => group.category.categoryId === activeCategory);

        if (!stillValid) {
            setActiveCategory(servicesByCategory[0].category.categoryId);
        }
    }, [servicesByCategory, activeCategory]);

    const formatPhone = (number) => {
        if (!number) return "";
        const digits = number.replace(/\D/g, "");

        if (digits.length === 11 && digits.startsWith("1")) {
            return "+1 (" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
        }

        return number.startsWith("+") ? number : "+" + number;
    };

    if (loading || !staff || !shopData) return <StaffPageSkeleton />;

    const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);

    const openBookingForService = (serviceId) => {
        setSelectedService(serviceId);
        setIsBookingOpen(true);
    };

    function formatTime24To12(time24) {
        if (!time24) return "";

        const [h, m] = time24.split(":").map(Number);
        const hour12 = h % 12 || 12;
        const minutes = m.toString().padStart(2, "0");
        const period = h >= 12 ? "PM" : "AM";

        return `${hour12}:${minutes} ${period}`;
    }

    const formatUrl = (url) => {
        if (!url) return "";
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        return `https://${url}`;
    };

    return (
        <div className="sf-staffpage">
            {/* HEADER */}
            <div className="sf-staffpage-div">
                <div className="sf-staffpage-header-row">
                    <button className="sf-back-button" onClick={() => navigate(`/shops/${shopId}`)}>
                        <ArrowLeft size={18} />
                    </button>

                    <div className="sf-staffpage-shop-name">{shopData.name}</div>
                </div>

                <div className="sf-header-clean">
                    <div className="sf-header-left">
                        <div className="sf-avatar-wrapper">
                            {staff.image ? (
                                <img src={staff.image} alt="" className="sf-avatar" />
                            ) : (
                                <div className="sf-avatar-fallback">{staff.firstName.charAt(0)}</div>
                            )}
                        </div>

                        <div className="sf-header-text">
                            <h1 className="sf-staff-name">
                                {staff.firstName} {staff.lastName}
                            </h1>

                            {staff.bio && <p className="sf-staff-bio">{staff.bio}</p>}
                        </div>
                    </div>
                </div>

                {/* SERVICES */}
                <div className="sf-services-section">
                    <div className="sf-category-row">
                        <h2 className="sf-section-title">Services</h2>
                        <span className="sf-category-count">{staff.services.length} services</span>
                    </div>

                    {/* ===== CATEGORY TABS ===== */}
                    <div className="sf-profile-tabs sf-profile-tabs--sticky">
                        {servicesByCategory.map(({ category }) => (
                            <button
                                key={category.categoryId}
                                className={`sf-profile-tab-btn ${
                                    activeCategory === category.categoryId ? "active" : ""
                                }`}
                                onClick={() => {
                                    setActiveCategory(category.categoryId);
                                    categoryRefs.current[category.categoryId]?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                    });
                                }}
                            >
                                {category.name?.[language]}
                            </button>
                        ))}
                    </div>

                    {/* ===== CATEGORY SECTIONS ===== */}
                    {servicesByCategory.map(({ category, services }) => (
                        <div
                            key={category.categoryId}
                            ref={(el) => (categoryRefs.current[category.categoryId] = el)}
                            className="sf-profile-category-section"
                        >
                            <h3 className="sf-profile-category-title">{category.name?.[language]}</h3>

                            {services.length > 0 ? (
                                services.map((s) => (
                                    <div key={s.serviceId}>
                                        <div className="sf-staffpage-service-item">
                                            <div className="sf-staffpage-service-info">
                                                <div className="sf-staffpage-service-name">{s.name}</div>
                                                <div className="sf-staffpage-service-duration">{s.description}</div>
                                            </div>

                                            <div className="sf-staffpage-service-right-area">
                                                <div className="sf-staffpage-service-right">
                                                    <div className="sf-staffpage-service-price">
                                                        {s.currency} {s.price}
                                                    </div>
                                                    <div className="sf-staffpage-service-duration">
                                                        {s.duration} min
                                                    </div>
                                                </div>

                                                <button
                                                    className="sf-staffpage-service-book-btn"
                                                    onClick={() => openBookingForService(s.serviceId)}
                                                >
                                                    {translations[language].staffPage.book}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="sf-service-divider-shop" />
                                    </div>
                                ))
                            ) : (
                                <div className="sf-empty-category">
                                    {language === "es"
                                        ? "No hay servicios en esta categoría"
                                        : "No services in this category"}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* SIDEBAR */}
            <div className="sf-staffpage-sidebar">
                {/* MAP */}
                <div className="sf-staffpage-sidebar-section">
                    <h3 className="sf-staffpage-sidebar-title">Location</h3>

                    <ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
                </div>

                {/* STAFF LIST */}
                <div className="sf-shop-sidebar-section">
                    <h3 className="sf-shop-sidebar-title">All Staff</h3>

                    <div className="sf-staffpage-staff-list">
                        {shopData.staffs?.map((b) => (
                            <div
                                className="sf-staffpage-staff-item"
                                key={b.staffId}
                                onClick={() => navigate(`/shops/${shopId}/staff/${b.staffId}`)}
                            >
                                {b.photo ? (
                                    <img className="sf-staffpage-staff-avatar" src={b.photo} alt={b.firstName} />
                                ) : (
                                    <div className="sf-staffpage-staff-avatar-fallback">
                                        <span>{b.firstName?.charAt(0).toUpperCase()}</span>
                                    </div>
                                )}
                                <div className="sf-staffpage-staff-name">
                                    {b.firstName} {b.lastName}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CONTACT */}
                <div className="sf-staffpage-sidebar-section">
                    <h3 className="sf-staffpage-sidebar-title">Contact</h3>

                    <p className="sf-staffpage-contact">
                        <Phone size={16} className="sf-staffpage-contact-icon" />
                        <span>{shopData.phone ? formatPhone(shopData.phone) : "No phone listed"}</span>
                    </p>

                    <hr className="sf-shop-divider" />

                    {/* HOURS */}
                    <div>
                        <h3 className="sf-staffpage-sidebar-title">Opening Hours</h3>

                        <div className="sf-shop-hours-full">
                            {shopData.openingHours.map((day, index) => {
                                const isToday = new Date().getDay() === index;
                                return (
                                    <div key={index} className={`sf-shop-hours-row ${isToday ? "is-today" : ""}`}>
                                        <div className="sf-shop-hours-day">{day.dayEN}</div>
                                        <div className="sf-shop-hours-time">
                                            <div className="sf-shop-hours-time">
                                                {day.closed
                                                    ? language === "es"
                                                        ? "Cerrado"
                                                        : "Closed"
                                                    : `${formatTime24To12(day.open)} - ${formatTime24To12(day.close)}`}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {shopData.socialLinks && Object.values(shopData.socialLinks).some((v) => v) && (
                        <div>
                            <hr className="sf-shop-divider" />

                            <h3 className="sf-staffpage-sidebar-title">
                                {translations[language].shopPage.sidebar.socialTitle}
                            </h3>

                            <div className="sf-shop-socials">
                                {shopData.socialLinks.instagram && (
                                    <div className="sf-social-item">
                                        <a
                                            href={formatUrl(shopData.socialLinks.instagram)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="sf-social-link"
                                        >
                                            <img
                                                src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png"
                                                alt="Instagram"
                                            />
                                        </a>
                                        <p>Instagram</p>
                                    </div>
                                )}

                                {shopData.socialLinks.facebook && (
                                    <div className="sf-social-item">
                                        <a
                                            href={formatUrl(shopData.socialLinks.facebook)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="sf-social-link"
                                        >
                                            <img
                                                src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png"
                                                alt="Facebook"
                                            />
                                        </a>
                                        <p>Facebook</p>
                                    </div>
                                )}

                                {shopData.socialLinks.tiktok && (
                                    <div className="sf-social-item">
                                        <a
                                            href={formatUrl(shopData.socialLinks.tiktok)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="sf-social-link"
                                        >
                                            <img
                                                src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png"
                                                alt="TikTok"
                                            />
                                        </a>
                                        <p>TikTok</p>
                                    </div>
                                )}

                                {shopData.socialLinks.website && (
                                    <div className="sf-social-item">
                                        <a
                                            href={formatUrl(shopData.socialLinks.website)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="sf-social-link"
                                        >
                                            <img
                                                src="https://cdn-icons-png.flaticon.com/512/545/545674.png"
                                                alt="Website"
                                            />
                                        </a>
                                        <p>{translations[language].shopPage.sidebar.websiteTitle}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* SOCIAL */}
            </div>
            {isBookingOpen && (
                <BookingModal
                    open={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    availabilityId={shopData.shopId}
                    shopData={shopData}
                    initialServiceId={selectedService}
                    initialstaffId={staff.staffId}
                />
            )}
        </div>
    );
};

export default StaffPage;
